import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Check, User } from 'lucide-react';
import AddressAutocomplete from '../components/tenant/AddressAutocomplete';
import ApplicationHomesMap from '../components/tenant/ApplicationHomesMap';
import CompactFileDropzone from '../components/tenant/CompactFileDropzone';
import DocumentHoverPreview, { DocumentInlinePreview } from '../components/tenant/DocumentHoverPreview';
import HeadshotFramer from '../components/tenant/HeadshotFramer';
import PickList from '../components/tenant/PickList';
import QueensFacultyProgramPicker, { type QueensAcademicSelection } from '../components/tenant/QueensFacultyProgramPicker';
import {
  facultyForcesProgramNa,
  isQueensStandardFaculty,
  QUEENS_FACULTY_NA,
  QUEENS_FACULTY_NONE,
  QUEENS_FACULTY_OTHER,
  QUEENS_PROGRAM_NA,
  queensFacultyPickerLabel,
  type QueensFacultyPickerId,
} from '../data/queensAcademicPickerConstants';
import { EXPECTED_GRADUATION_MONTH_OPTIONS } from '../data/expectedGraduationOptions';
import { programsForQueensFaculty, QUEENS_FACULTY_OPTIONS, queensFacultyLabel } from '../data/queensUniversityAcademics';
import { useTenantApiToken } from '../contexts/TenantProfileContext';
import { GOOGLE_ADS_EVENTS, GOOGLE_ADS_ONCE_KEYS } from '@shared/utils/google-ads';
import { trackTenantGoogleAdsOnce } from '../utils/googleAdsClick';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import {
  addProspectApplicationUnit,
  addProspectIncome,
  addProspectReference,
  fetchProspectApplicationFile,
  getProspectApplication,
  getProspectApplicationChecks,
  type ProspectApplicationChecks,
  removeProspectApplicationDocument,
  removeProspectApplicationUnit,
  removeProspectIncome,
  removeProspectReference,
  updateProspectIncome,
  saveProspectApplicant,
  saveProspectReferral,
  submitProspectApplication,
  uploadProspectApplicationFile,
  type ProspectApplicationDocumentKind,
  type ProspectApplicationHome,
  type ProspectApplicationIncome,
  type ProspectApplicationPage,
  type ProspectApplicationPerson,
  type ProspectGovernmentIdKind,
  type ProspectIncomeKind,
  type ProspectSubmissionOutcome,
  type ProspectSupplementalSuggestion,
} from '../services/tenantApi';
import { cachedProspectDocument, loadProspectDocument } from '../utils/application-document-cache';
import {
  academicChecksFromFields,
  addressSectionDone,
  applicationProgress,
  basicSectionDone,
  type ApplicationProgress,
  type ApplicationTaskChecks,
  birthdayIso,
  birthdayPartsFromIso,
  contactSectionDone,
  daysInMonth,
  emergencySectionDone,
  emptyBirthday,
  liveTaskChecks,
  phoneComplete,
  rentShareTotal,
  type BirthdayParts,
} from '../utils/application-progress';
import { formatPhoneInputLive } from '../utils/phone-display';
import {
  acknowledgeApplicantDraft,
  rememberApplicantDraft,
  savableApplicantPatch,
  unackedApplicantPatch,
  type SavableApplicantPatch,
} from '../utils/prospect-applicant-draft-store';
import { isAutosaveNetworkError, useAutosave } from '../hooks/useAutosave';
import { useCardSaveBadges } from '../hooks/useCardSaveBadges';
import { CardSaveBadge } from '../components/tenant/CardSaveBadge';
import { ReferralCard } from '../components/tenant/ReferralCard';
import { PersonAvatar } from '../components/tenant/PersonAvatar';
import { ProgressTrack } from '../components/tenant/ProgressTrack';
import { ProspectPersonSwitcher } from '../components/tenant/ProspectPersonSwitcher';
import { tenantGoogleMapsLoader } from '../utils/tenantGoogleMapsLoader';
import { buildApplicationHomeMapPins, shortlistPriorityLabel } from '../utils/application-homes-map';

const SHELL = 'w-full';
const CARD = 'rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 p-4 shadow-bento sm:p-5';
const INPUT = 'mt-1 w-full rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';
const CELL_INPUT = 'w-full rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';
const INCOME_GRID =
  'grid grid-cols-1 gap-3 px-3 py-3 max-sm:rounded-lg max-sm:border max-sm:border-gray-100 max-sm:bg-gray-50/80 dark:max-sm:border-white/5 dark:max-sm:bg-white/[0.02] sm:grid-cols-[13rem_minmax(0,1fr)_10rem_5rem] sm:items-center sm:gap-0 sm:rounded-none sm:border-0 sm:bg-transparent sm:px-0 sm:py-2 dark:sm:bg-transparent';
const INCOME_CELL = 'min-w-0 sm:px-3';
const INCOME_MOBILE_LABEL =
  'mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden';
const STATUS_INPUT_BOX =
  'mt-1 w-full rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-2 text-sm';
const ADDRESS_INPUT = 'mt-1 w-full rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';
const LABEL = 'block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400';
const FRIEND_PROGRESS_POLL_MS = 30_000;

const SCHOOL_YEAR_OPTIONS = [
  { value: '1st', label: '1st year' },
  { value: '2nd', label: '2nd year' },
  { value: '3rd', label: '3rd year' },
  { value: '4th', label: '4th year' },
  { value: 'Graduate', label: 'Graduate' },
  { value: 'NA', label: 'N/A' },
  { value: 'Other', label: 'Other' },
];

const KNOWN_SCHOOL_YEARS = new Set(['1st', '2nd', '3rd', '4th', 'Graduate', 'NA']);

function schoolYearChoice(value: string): { choice: string; other: string } {
  if (!value) return { choice: '', other: '' };
  if (KNOWN_SCHOOL_YEARS.has(value)) return { choice: value, other: '' };
  return { choice: 'Other', other: value };
}

function academicsFromPerson(person: { school: string; program: string; expectedGraduationTerm: string; expectedGraduationYear: string }): QueensAcademicSelection {
  const school = person.school.trim();
  const program = person.program.trim();
  let facultyId: QueensFacultyPickerId | null = null;
  let facultyCustom = '';
  if (school === "No faculty at Queen's") facultyId = QUEENS_FACULTY_NONE;
  else if (school === 'N/A') facultyId = QUEENS_FACULTY_NA;
  else {
    const match = QUEENS_FACULTY_OPTIONS.find((faculty) => faculty.label === school);
    if (match) facultyId = match.id;
    else if (school) {
      facultyId = QUEENS_FACULTY_OTHER;
      facultyCustom = school;
    }
  }
  let programKind: QueensAcademicSelection['programKind'] = null;
  let programListValue: string | null = null;
  let programOtherText = '';
  if (facultyForcesProgramNa(facultyId) || program === 'N/A') {
    programKind = 'na';
  } else if (isQueensStandardFaculty(facultyId) && programsForQueensFaculty(facultyId).includes(program)) {
    programKind = 'list';
    programListValue = program;
  } else if (program) {
    programKind = 'other';
    programOtherText = program;
  }
  const month = EXPECTED_GRADUATION_MONTH_OPTIONS.find((option) => option.value === person.expectedGraduationTerm)?.value ?? null;
  return {
    facultyId,
    facultyCustom,
    programKind,
    programListValue,
    programOtherText,
    expectedGraduationMonth: month,
    expectedGraduationYear: person.expectedGraduationYear.trim() || null,
  };
}

function personFieldsFromAcademics(selection: QueensAcademicSelection): {
  school: string;
  program: string;
  expectedGraduationTerm: string;
  expectedGraduationYear: string;
} {
  const school = queensFacultyPickerLabel(
    selection.facultyId,
    selection.facultyCustom,
    isQueensStandardFaculty(selection.facultyId) ? queensFacultyLabel(selection.facultyId) : null,
  ) ?? '';
  let program = '';
  if (facultyForcesProgramNa(selection.facultyId) || selection.programKind === 'na') program = QUEENS_PROGRAM_NA;
  else if (selection.programKind === 'other') program = selection.programOtherText.trim();
  else program = selection.programListValue ?? '';
  return {
    school,
    program,
    expectedGraduationTerm: selection.expectedGraduationMonth ?? '',
    expectedGraduationYear: selection.expectedGraduationYear ?? '',
  };
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHOTO_ID_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif', 'bmp', 'tif', 'tiff']);
const PHOTO_ID_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/tiff',
]);
const SUPPLEMENTAL_EXTENSIONS = PHOTO_ID_EXTENSIONS;
const SUPPLEMENTAL_MIME_TYPES = PHOTO_ID_MIME_TYPES;
const DOCUMENT_FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.bmp,.tif,.tiff,application/pdf,image/jpeg,image/png,image/webp,image/gif,image/heic,image/bmp,image/tiff';
const DOCUMENT_FILE_HINT = 'PDF, JPEG, PNG, WebP, GIF, HEIC, BMP, or TIFF.';
const BLOCKED_EXTENSIONS = new Set([
  'js', 'mjs', 'cjs', 'exe', 'bat', 'cmd', 'com', 'scr', 'msi', 'dll', 'app', 'dmg',
  'sh', 'bash', 'ps1', 'php', 'html', 'htm', 'svg', 'xml', 'jar', 'vbs', 'wsf', 'zip', 'rar',
]);

const INCOME_OPTIONS: Array<{ value: ProspectIncomeKind; label: string }> = [
  { value: 'EMPLOYMENT', label: 'Job' },
  { value: 'SCHOLARSHIP', label: 'Scholarship' },
  { value: 'PARENT_CONTRIBUTION', label: 'Parental Contribution' },
  { value: 'OSAP', label: 'OSAP' },
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'OTHER', label: 'Other' },
];

const OSAP_OPTIONS = [
  { value: 'GRANT', label: 'Grant' },
  { value: 'LOAN', label: 'Loan' },
];

interface IncomeDraft {
  kind: ProspectIncomeKind | '';
  osapCategory: string;
  label: string;
  employerName: string;
  rentSharePercent: string;
}

function emptyIncomeDraft(kind: ProspectIncomeKind | '' = ''): IncomeDraft {
  return { kind, osapCategory: '', label: '', employerName: '', rentSharePercent: '' };
}

function incomeShareReady(value: string): boolean {
  const cleaned = value.replace(/[%\s]/g, '');
  return /^\d+(\.\d{1,2})?$/.test(cleaned) && Number(cleaned) > 0 && Number(cleaned) <= 100;
}

function incomeRowReady(draft: IncomeDraft): boolean {
  if (!draft.kind || !incomeShareReady(draft.rentSharePercent)) return false;
  if (draft.kind === 'OSAP') return draft.osapCategory === 'GRANT' || draft.osapCategory === 'LOAN';
  if (draft.kind === 'SAVINGS') return true;
  if (draft.kind === 'EMPLOYMENT') return Boolean(draft.label.trim() && draft.employerName.trim());
  return Boolean(draft.label.trim());
}

function incomeDetailsText(source: ProspectApplicationIncome): string {
  if (source.kind === 'OSAP') {
    if (source.osapCategory === 'GRANT') return 'Grant';
    if (source.osapCategory === 'LOAN') return 'Loan';
    return '—';
  }
  if (source.kind === 'SAVINGS') return '—';
  if (source.kind === 'EMPLOYMENT') {
    const parts = [source.label, source.employerName].filter(Boolean);
    if (parts.length > 0) return parts.join(' · ');
  }
  return source.label || source.details || '—';
}

function formatShare(share: number | null): string {
  if (share == null || !Number.isFinite(share)) return '—';
  const shown = Number.isInteger(share) ? String(share) : String(Math.round(share * 100) / 100);
  return `${shown}%`;
}

function draftFromIncome(source: ProspectApplicationIncome): IncomeDraft {
  return {
    kind: source.kind,
    osapCategory: source.osapCategory,
    label: source.label,
    employerName: source.employerName,
    rentSharePercent: source.rentSharePercent == null ? '' : String(source.rentSharePercent),
  };
}

function draftForKind(kind: ProspectIncomeKind, current: IncomeDraft): IncomeDraft {
  const keepLabel = kind === 'EMPLOYMENT' || kind === 'SCHOLARSHIP' || kind === 'PARENT_CONTRIBUTION' || kind === 'OTHER';
  return {
    kind,
    osapCategory: kind === 'OSAP' && (current.osapCategory === 'GRANT' || current.osapCategory === 'LOAN') ? current.osapCategory : '',
    label: keepLabel ? current.label : '',
    employerName: kind === 'EMPLOYMENT' ? current.employerName : '',
    rentSharePercent: current.rentSharePercent,
  };
}

function incomeMatches(source: ProspectApplicationIncome, row: IncomeDraft): boolean {
  if (!row.kind || source.kind !== row.kind) return false;
  const share = Number(row.rentSharePercent.replace(/[%\s]/g, ''));
  if (source.rentSharePercent !== share) return false;
  if (row.kind === 'OSAP') return source.osapCategory === row.osapCategory;
  if (row.kind === 'SAVINGS') return true;
  if (row.kind === 'EMPLOYMENT') return source.label === row.label.trim() && source.employerName === row.employerName.trim();
  return source.label === row.label.trim();
}

function IncomeDetailFields({
  draft,
  onChange,
  autoFocus = false,
  onBlur,
}: {
  draft: IncomeDraft;
  onChange: (next: IncomeDraft) => void;
  autoFocus?: boolean;
  onBlur?: () => void;
}) {
  function finishOnEnter(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || !onBlur) return;
    event.preventDefault();
    onBlur();
  }

  if (draft.kind === 'OSAP') {
    return (
      <PickList
        autoOpen={autoFocus}
        placeholder="Grant or loan"
        value={draft.osapCategory}
        options={OSAP_OPTIONS}
        onChange={(osapCategory) => onChange({ ...draft, osapCategory, label: '' })}
      />
    );
  }
  if (draft.kind === 'PARENT_CONTRIBUTION') {
    return (
      <input
        autoFocus={autoFocus}
        className={CELL_INPUT}
        placeholder="Parent's name"
        value={draft.label}
        onChange={(event) => onChange({ ...draft, label: event.target.value })}
        onBlur={onBlur}
        onKeyDown={finishOnEnter}
      />
    );
  }
  if (draft.kind === 'EMPLOYMENT') {
    return (
      <div
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
        onBlur={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
          onBlur?.();
        }}
      >
        <input
          autoFocus={autoFocus}
          className={CELL_INPUT}
          placeholder="Job title"
          value={draft.label}
          onChange={(event) => onChange({ ...draft, label: event.target.value })}
          onKeyDown={finishOnEnter}
        />
        <input
          className={CELL_INPUT}
          placeholder="Employer"
          value={draft.employerName}
          onChange={(event) => onChange({ ...draft, employerName: event.target.value })}
          onKeyDown={finishOnEnter}
        />
      </div>
    );
  }
  if (draft.kind === 'SCHOLARSHIP') {
    return (
      <input
        autoFocus={autoFocus}
        className={CELL_INPUT}
        placeholder="Scholarship name"
        value={draft.label}
        onChange={(event) => onChange({ ...draft, label: event.target.value })}
        onBlur={onBlur}
        onKeyDown={finishOnEnter}
      />
    );
  }
  if (draft.kind === 'OTHER') {
    return (
      <input
        autoFocus={autoFocus}
        className={CELL_INPUT}
        placeholder="What is this"
        value={draft.label}
        onChange={(event) => onChange({ ...draft, label: event.target.value })}
        onBlur={onBlur}
        onKeyDown={finishOnEnter}
      />
    );
  }
  return null;
}

const INCOME_VALUE = 'block w-full rounded-md text-left hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-white/5';

const EMERGENCY_RELATIONSHIPS = ['Father', 'Mother', 'Aunt', 'Uncle', 'Sibling', 'Coworker'];
const REFERENCE_OPTIONS = [
  { value: 'Landlord', label: 'Landlord (best)' },
  { value: 'Employer', label: 'Employer (second best)' },
  { value: 'Coach', label: 'Coach' },
  { value: 'Teacher', label: 'Teacher' },
  { value: 'Professor', label: 'Professor' },
  { value: 'Mentor', label: 'Mentor' },
  { value: 'Family friend', label: 'Family friend' },
];

function formatCallWindow(start: string, end: string): string {
  const label = (value: string) => {
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match) return '';
    const hour = Number(match[1]);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    return `${hour % 12 || 12}:${match[2]} ${suffix}`;
  };
  const startLabel = label(start);
  const endLabel = label(end);
  if (!startLabel || !endLabel) return '';
  return `${startLabel}–${endLabel} ET`;
}

function callWindowReady(start: string, end: string): boolean {
  const minutes = (value: string) => {
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  };
  const startMinutes = minutes(start);
  const endMinutes = minutes(end);
  return startMinutes !== null && endMinutes !== null && endMinutes > startMinutes;
}

const GOVERNMENT_ID_OPTIONS: Array<{ value: ProspectGovernmentIdKind; label: string }> = [
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'DRIVERS_LICENSE', label: "Driver's license" },
  { value: 'ID_CARD', label: 'ID card' },
];

const SUGGESTION_OPTIONS: Array<{ value: ProspectSupplementalSuggestion; label: string }> = [
  { value: 'CURRENT_HOME_PHOTO', label: 'A photo of your room, or the home you currently care for' },
  { value: 'PERSONAL_BIO', label: 'A short bio' },
  { value: 'COVER_LETTER', label: 'Cover letter' },
  { value: 'OTHER', label: 'Other' },
];

function governmentIdTypeLabel(name: string | null): string {
  if (!name) return '';
  const known = GOVERNMENT_ID_OPTIONS.find((option) => name.toLowerCase().includes(option.label.toLowerCase()));
  return known?.label ?? '';
}

function suggestionLabel(value: ProspectSupplementalSuggestion | null): string {
  if (value === 'OSAP_FUNDING') return 'OSAP funding';
  return SUGGESTION_OPTIONS.find((option) => option.value === value)?.label ?? '';
}

const OUTCOME_LABEL: Record<ProspectSubmissionOutcome, string> = {
  PENDING: 'Submitted',
  ACCEPTED: 'Accepted',
  DENIED: 'Denied',
};

/** Side-by-side apply-homes map (sm+); stacked below the list on narrow viewports. */
function useShowApplyHomesMap(active: boolean): boolean {
  const [wideEnough, setWideEnough] = useState(false);
  useLayoutEffect(() => {
    const query = window.matchMedia('(min-width: 640px)');
    const update = () => setWideEnough(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return active && wideEnough;
}

function TruncatedHomeLabel({
  label,
  muted,
  nowrap = false,
}: {
  label: string;
  muted?: boolean;
  nowrap?: boolean;
}): ReactElement {
  const tooltipId = useId();
  const textClass = muted ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100';
  return (
    <span className={`group/label relative ${nowrap ? '' : 'min-w-0'}`}>
      <span
        aria-describedby={tooltipId}
        className={`block text-sm font-medium ${nowrap ? 'whitespace-nowrap' : 'truncate'} ${textClass}`}
      >
        {label}
      </span>
      <span
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none invisible absolute bottom-[calc(100%+0.35rem)] left-0 z-50 w-max max-w-[min(100vw-2rem,20rem)] rounded-lg border border-gray-200 bg-gray-900 px-3 py-2 text-left text-xs font-normal leading-snug text-white shadow-lg ring-1 ring-black/5 group-hover/label:visible group-focus-within/label:visible dark:border-white/10 dark:bg-gray-950 dark:text-gray-100 dark:ring-white/10"
      >
        {label}
      </span>
    </span>
  );
}

function homeStatusLabel(home: { linked: boolean; submissions: Array<{ outcome: ProspectSubmissionOutcome }> }): string {
  const latest = home.submissions[0];
  if (latest) return OUTCOME_LABEL[latest.outcome];
  return '';
}

function ApplyHomePickerRow({
  home,
  busy,
  showRank,
  showStatus,
  onToggle,
}: {
  home: ProspectApplicationHome;
  busy: boolean;
  showRank: boolean;
  showStatus: boolean;
  onToggle: (home: ProspectApplicationHome) => void;
}): ReactElement {
  const tourBlocked = !home.tourComplete && !home.linked;
  const status = homeStatusLabel(home);
  const actionLabel = home.linked ? 'Remove' : tourBlocked ? 'Tour required' : 'Add';
  return (
    <li
      className={`flex w-full min-w-0 flex-col items-stretch gap-2.5 rounded-xl border px-3 py-3 sm:flex-row sm:items-center sm:gap-3 sm:py-2 ${
        tourBlocked
          ? 'border-gray-200 bg-gray-100 text-gray-400 dark:border-white/10 dark:bg-gray-950 dark:text-gray-500'
          : home.linked
            ? 'border-emerald-300 bg-white dark:border-emerald-500/40 dark:bg-gray-950'
            : 'border-gray-200 bg-white dark:border-white/10 dark:bg-gray-950'
      }`}
    >
      {showRank ? (
        home.shortlistRank ? (
          <p
            className={`hidden w-[4.75rem] shrink-0 text-xs font-semibold sm:block ${
              tourBlocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'
            }`}
          >
            {shortlistPriorityLabel(home.shortlistRank)}
          </p>
        ) : (
          <p className="hidden w-[4.75rem] shrink-0 sm:block" aria-hidden="true">
            {' '}
          </p>
        )
      ) : null}
      <div className="min-w-0 flex-1">
        {home.shortlistRank ? (
          <p
            className={`mb-0.5 text-[10px] font-semibold uppercase tracking-wide sm:hidden ${
              tourBlocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {shortlistPriorityLabel(home.shortlistRank)}
          </p>
        ) : null}
        <TruncatedHomeLabel label={home.label} muted={tourBlocked} />
      </div>
      {showStatus ? (
        status ? (
          <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400 sm:order-none">{status}</p>
        ) : (
          <p className="hidden shrink-0 text-xs sm:invisible sm:block" aria-hidden="true">
            Submitted
          </p>
        )
      ) : null}
      <button
        type="button"
        disabled={busy || tourBlocked}
        title={
          tourBlocked ? 'Tour required to apply' : home.linked ? 'Remove from application' : 'Add to application'
        }
        onClick={() => {
          if (tourBlocked) return;
          onToggle(home);
        }}
        className={`grid w-full shrink-0 place-items-center rounded-lg px-2.5 py-2 text-sm sm:w-auto sm:px-3 sm:py-1.5 ${
          tourBlocked
            ? 'cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
            : home.linked
              ? 'border border-emerald-700 bg-white text-emerald-800 dark:border-emerald-400 dark:bg-gray-900 dark:text-emerald-300'
              : 'bg-gray-900 text-white'
        }`}
      >
        {actionLabel}
      </button>
    </li>
  );
}

const CURRENT_YEAR = new Date().getFullYear();
/** Youngest selectable birth year — applicants must be at least this age today. */
const MIN_APPLICANT_AGE_YEARS = 15;

function youngestApplicantBirthYear(asOf = new Date()): number {
  const cutoff = new Date(asOf);
  cutoff.setFullYear(cutoff.getFullYear() - MIN_APPLICANT_AGE_YEARS);
  return cutoff.getFullYear();
}

const YEAR_OPTIONS = Array.from({ length: 85 }, (_, index) => {
  const year = String(youngestApplicantBirthYear() - index);
  return { value: year, label: year };
});
const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function statusWord(done: boolean): string {
  return done ? 'Complete' : 'Incomplete';
}

function statusTextClass(done: boolean): string {
  return done
    ? 'font-medium text-emerald-700 dark:text-emerald-300'
    : 'font-medium text-rose-600 dark:text-rose-300';
}

function StatusReadout({ done, className }: { done: boolean; className?: string }) {
  return (
    <div className={`${STATUS_INPUT_BOX} ${statusTextClass(done)} ${className ?? ''}`.trim()} role="status">
      {statusWord(done)}
    </div>
  );
}

function coApplicantIncompleteMessage(displayName: string): string {
  return `${displayName} needs to fill this out.`;
}

const READONLY_STATUS_BOX =
  'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5';

function IncompleteCoApplicantHint({
  done,
  ownerDisplayName,
  children,
  className,
}: {
  done: boolean;
  ownerDisplayName?: string;
  children: ReactNode;
  className?: string;
}) {
  if (done || !ownerDisplayName) {
    return <>{children}</>;
  }
  const message = coApplicantIncompleteMessage(ownerDisplayName);
  return (
    <span className={`group relative block ${className ?? ''}`.trim()}>
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 w-max max-w-[14rem] -translate-x-1/2 rounded-lg bg-gray-900 px-2.5 py-1.5 text-center text-xs font-normal normal-case tracking-normal text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 dark:bg-gray-100 dark:text-gray-900"
      >
        {message}
      </span>
    </span>
  );
}

function StatusValue({
  label,
  done,
  incompleteOwnerName,
}: {
  label?: string;
  done: boolean;
  incompleteOwnerName?: string;
}) {
  const readout = (
    <IncompleteCoApplicantHint done={done} ownerDisplayName={incompleteOwnerName}>
      <StatusReadout done={done} className={label ? undefined : 'mt-0'} />
    </IncompleteCoApplicantHint>
  );
  if (!label) return readout;
  return <Field label={label}>{readout}</Field>;
}

function IncomeLabeledCell({
  label,
  children,
  className,
  hideMobileLabel,
}: {
  label: string;
  children: ReactNode;
  className?: string;
  hideMobileLabel?: boolean;
}) {
  return (
    <div className={`${INCOME_CELL} ${className ?? ''}`.trim()} role="cell">
      {hideMobileLabel ? null : <span className={INCOME_MOBILE_LABEL}>{label}</span>}
      {children}
    </div>
  );
}

function IncomeTableHeader() {
  return (
    <div
      className="hidden border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-white/10 dark:text-gray-400 sm:grid sm:grid-cols-[13rem_minmax(0,1fr)_10rem_5rem] sm:items-center"
      role="row"
    >
      <div className="px-3 py-2" role="columnheader">Source</div>
      <div className="px-3 py-2" role="columnheader">Details</div>
      <div className="px-3 py-2" role="columnheader">Share of rent</div>
      <div className="px-3 py-2" role="columnheader"><span className="sr-only">Actions</span></div>
    </div>
  );
}

function IncomeReadOnlyStatusCell({
  done,
  incompleteOwnerName,
  className,
}: {
  done: boolean;
  incompleteOwnerName: string;
  className?: string;
}) {
  return (
    <IncompleteCoApplicantHint done={done} ownerDisplayName={incompleteOwnerName}>
      <div className={`${READONLY_STATUS_BOX} ${statusTextClass(done)} ${className ?? ''}`.trim()} role="status">
        {statusWord(done)}
      </div>
    </IncompleteCoApplicantHint>
  );
}

function IncomeCoApplicantTable({ done, incompleteOwnerName }: { done: boolean; incompleteOwnerName: string }) {
  return (
    <div
      className="mt-3 overflow-visible rounded-xl border border-gray-200 bg-white text-sm dark:border-white/10 dark:bg-gray-700"
      role="table"
    >
      <IncomeTableHeader />
      <div className={`${INCOME_GRID} border-b border-gray-100 dark:border-white/5`} role="row">
        <IncomeLabeledCell label="Source">
          <IncomeReadOnlyStatusCell done={done} incompleteOwnerName={incompleteOwnerName} />
        </IncomeLabeledCell>
        <IncomeLabeledCell label="Details">
          <IncomeReadOnlyStatusCell done={done} incompleteOwnerName={incompleteOwnerName} />
        </IncomeLabeledCell>
        <IncomeLabeledCell label="Share of rent">
          <IncomeReadOnlyStatusCell done={done} incompleteOwnerName={incompleteOwnerName} className="font-semibold" />
        </IncomeLabeledCell>
      </div>
      <div
        className={`${INCOME_GRID} border-y border-gray-200 bg-gray-200 text-gray-900 dark:border-white/10 dark:bg-gray-600 dark:text-white max-sm:mx-0 max-sm:rounded-none max-sm:border-x-0`}
        role="row"
      >
        <div className={`${INCOME_CELL} font-semibold max-sm:col-span-full`} role="cell">
          <span className={INCOME_MOBILE_LABEL}>Total</span>
          <span className="hidden sm:inline">Total</span>
        </div>
        <div className={`${INCOME_CELL} hidden sm:block`} role="cell" aria-hidden />
        <IncomeLabeledCell label="Share of rent" className="max-sm:col-span-full font-semibold">
          <IncomeReadOnlyStatusCell done={done} incompleteOwnerName={incompleteOwnerName} className="font-semibold" />
        </IncomeLabeledCell>
        <div className={`${INCOME_CELL} hidden sm:block`} role="cell" aria-hidden />
      </div>
    </div>
  );
}

function SectionCheck({ done }: { done: boolean }) {
  if (!done) return null;
  return (
    <span className="absolute right-4 top-4 z-10 grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700 sm:right-5 sm:top-5" aria-label="Complete">
      <Check size={16} strokeWidth={2.5} aria-hidden="true" />
    </span>
  );
}

function emailProblem(value: string): string | null {
  const email = value.trim();
  if (!email) return null;
  if (email.length > 254 || !EMAIL_PATTERN.test(email)) return 'Enter a valid email address.';
  return null;
}

function supplementalFileProblem(file: File): string | null {
  const parts = file.name.toLowerCase().split(/[/\\]/).pop()?.split('.') ?? [];
  const extensions = parts.slice(1);
  const extension = extensions[extensions.length - 1] ?? '';
  if (!extension || extensions.some((part) => BLOCKED_EXTENSIONS.has(part))) {
    return 'Scripts and other executable files are not accepted.';
  }
  if (!SUPPLEMENTAL_EXTENSIONS.has(extension) || (file.type && !SUPPLEMENTAL_MIME_TYPES.has(file.type))) {
    return 'Use a PDF or a photo.';
  }
  return null;
}

function identityFileProblem(file: File): string | null {
  const parts = file.name.toLowerCase().split(/[/\\]/).pop()?.split('.') ?? [];
  const extensions = parts.slice(1);
  const extension = extensions[extensions.length - 1] ?? '';
  if (!extension || extensions.some((part) => BLOCKED_EXTENSIONS.has(part))) {
    return 'Scripts and other executable files are not accepted.';
  }
  if (!PHOTO_ID_EXTENSIONS.has(extension) || (file.type && !PHOTO_ID_MIME_TYPES.has(file.type))) {
    return 'Use a PDF or a photo: JPEG, PNG, WebP, GIF, HEIC, BMP, or TIFF.';
  }
  return null;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      {children}
    </label>
  );
}

interface SubmissionBlocker {
  name: string;
  items: string[];
}

function missingSubmitPieces(checks: ApplicationTaskChecks): string[] {
  const missing: string[] = [];
  if (!(checks.firstName && checks.preferredFirstName && checks.lastName)) missing.push('Name');
  if (!(checks.birthYear && checks.birthMonth && checks.birthDay)) missing.push('Birthday');
  if (!(checks.street && checks.city && checks.province && checks.postalCode)) missing.push('Address');
  if (!(checks.emergencyName && checks.emergencyRelationship && checks.emergencyPhone && checks.emergencyEmail)) missing.push('Emergency contact');
  if (!checks.income) missing.push('How you will pay rent');
  if (!checks.photoId) missing.push('Government ID');
  if (!checks.headshot) missing.push('Headshot');
  return missing;
}

function submissionBlockers(
  applicants: ProspectApplicationPerson[],
  homes: ProspectApplicationHome[],
  draft: ProspectApplicationPerson,
  birthday: BirthdayParts,
): SubmissionBlocker[] {
  const people = applicants.flatMap((applicant) => {
    const viewingSelf = draft.isViewer && applicant.applicantId === draft.applicantId;
    const checks = viewingSelf
      ? liveTaskChecks(draft, birthday)
      : applicant.taskChecks;
    const items = missingSubmitPieces(checks);
    return items.length > 0 ? [{ name: applicant.displayName, items }] : [];
  });
  const linked = homes.filter((home) => home.linked);
  if (linked.length === 0) {
    return [...people, { name: 'Application', items: ['Add a home'] }];
  }
  const stillOpen = linked.some((home) => {
    const latest = [...home.submissions].sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))[0];
    return latest?.outcome !== 'PENDING' && latest?.outcome !== 'ACCEPTED';
  });
  if (!stillOpen) {
    return [...people, { name: 'Application', items: ['These homes are already submitted'] }];
  }
  return people;
}

function SubmitApplicationButton({
  busy,
  blockers,
  onSubmit,
}: {
  busy: boolean;
  blockers: SubmissionBlocker[];
  onSubmit: () => void;
}) {
  const blocked = blockers.length > 0;
  return (
    <div className="group relative w-full min-[640px]:w-auto min-[640px]:shrink-0">
      <button
        type="button"
        aria-disabled={blocked || busy}
        className={`w-full whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium min-[640px]:w-auto ${
          blocked
            ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-white/10 dark:text-gray-500'
            : 'bg-gray-900 text-white disabled:opacity-40'
        }`}
        disabled={busy && !blocked}
        onClick={() => {
          if (blocked || busy) return;
          onSubmit();
        }}
      >
        Submit application
      </button>
      {blocked ? (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-30 mt-2 hidden w-72 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-bento group-hover:block group-focus-within:block dark:border-white/10 dark:bg-gray-800"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Still needed</p>
          <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto text-sm text-gray-700 dark:text-gray-300">
            {blockers.map((blocker) => (
              blocker.name === 'Application' ? (
                <li key={blocker.name} className="list-disc pl-4 font-medium text-gray-900 dark:text-white">{blocker.items.join(', ')}</li>
              ) : (
                <li key={blocker.name}>
                  <p className="font-medium text-gray-900 dark:text-white">{blocker.name}</p>
                  <ul className="mt-0.5 list-disc pl-4">
                    {blocker.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </li>
              )
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function SuggestField({
  label,
  value,
  suggestions,
  placeholder,
  onChange,
}: {
  label?: string;
  value: string;
  suggestions: string[];
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const typed = value.trim().toLowerCase();
  const matches = suggestions.filter((option) => option.toLowerCase() !== typed && option.toLowerCase().includes(typed));
  const control = (
    <div className="relative">
      <input
        className={label ? INPUT : `${INPUT} mt-0`}
        value={value}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(event) => onChange(event.target.value)}
      />
      {open && matches.length > 0 ? (
        <ul className="absolute z-40 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-950 py-1 shadow-lg">
          {matches.map((option) => (
            <li key={option}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5"
                onMouseDown={(event) => {
                  event.preventDefault();
                  onChange(option);
                  setOpen(false);
                }}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
  if (!label) return control;
  return <Field label={label}>{control}</Field>;
}

export default function ApplyToUnitPage() {
  const getToken = useTenantApiToken();
  const setPageProgress = useSetPageProgress();
  const [page, setPage] = useState<ProspectApplicationPage | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProspectApplicationPerson | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [referenceDraft, setReferenceDraft] = useState({ name: '', relationship: '', phone: '', callStartTime: '', callEndTime: '' });
  const [incomeDraft, setIncomeDraft] = useState<IncomeDraft>(emptyIncomeDraft);
  const [incomeEdit, setIncomeEdit] = useState<{ id: string; field: 'kind' | 'details' | 'share'; draft: IncomeDraft } | null>(null);
  const incomeEditRef = useRef(incomeEdit);
  useEffect(() => {
    incomeEditRef.current = incomeEdit;
  }, [incomeEdit]);
  const [pendingSupplemental, setPendingSupplemental] = useState<{ file: File; type: ProspectSupplementalSuggestion | ''; name: string } | null>(null);
  const [pendingId, setPendingId] = useState<{ file: File; kind: ProspectGovernmentIdKind | '' } | null>(null);
  const [headshotSource, setHeadshotSource] = useState<Blob | null>(null);
  const [headshotPreview, setHeadshotPreview] = useState<{ id: string; url: string } | null>(null);
  const [referrerName, setReferrerName] = useState('');
  const [referrerSource, setReferrerSource] = useState('');
  const [birthdayParts, setBirthdayParts] = useState<BirthdayParts>(emptyBirthday());
  const [yearIsOther, setYearIsOther] = useState(false);
  const hydrated = useRef(false);
  const lastSaved = useRef('');
  const incomeApplicantRef = useRef<string | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const viewerDraftRef = useRef<ProspectApplicationPerson | null>(null);
  const fullLoadVersion = useRef(0);
  const load = useCallback(async () => {
    const next = await getProspectApplication(getToken);
    fullLoadVersion.current += 1;
    setPage(next);
    setReferrerName(next.referralName ?? '');
    setReferrerSource(next.referralSource ?? '');
    return next;
  }, [getToken]);

  useEffect(() => {
    void load().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Unable to load the application.');
    });
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    const pull = () => {
      if (cancelled || inFlight || document.visibilityState === 'hidden' || fullLoadVersion.current === 0) return;
      const version = fullLoadVersion.current;
      inFlight = true;
      void getProspectApplicationChecks(getToken)
        .then((checks) => {
          if (cancelled || fullLoadVersion.current !== version) return;
          setPage((current) => (current?.open ? applyFriendChecks(current, checks) : current));
        })
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
        });
    };
    const interval = window.setInterval(pull, FRIEND_PROGRESS_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') pull();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [getToken]);

  const selected = useMemo(() => {
    if (!page) return null;
    return page.applicants.find((applicant) => applicant.applicantId === selectedId) ?? page.applicants.find((applicant) => applicant.isViewer) ?? page.applicants[0] ?? null;
  }, [page, selectedId]);

  useEffect(() => {
    if (!selected) return;
    setSelectedId(selected.applicantId);
    const next = personDraftFromSelection(draftRef.current, selected, lastSaved, viewerDraftRef);
    if (incomeApplicantRef.current !== selected.applicantId) {
      incomeApplicantRef.current = selected.applicantId;
      setIncomeEdit(null);
      setHeadshotSource(null);
      setYearIsOther(false);
      setBirthdayParts(next.isViewer ? birthdayPartsFromIso(next.dateOfBirth) : emptyBirthday());
    }
    setDraft(next);
    hydrated.current = true;
  }, [selected]);

  if (draft?.isViewer) viewerDraftRef.current = draft;
  const editable = Boolean(draft && page?.open && draft.isViewer);
  const coApplicantOwnerName = !editable && draft ? draft.displayName : undefined;
  const contactEmailProblem = draft ? emailProblem(draft.email) : null;
  const emergencyEmailProblem = draft ? emailProblem(draft.emergencyContactEmail) : null;
  const homeAdded = Boolean(page?.homes.some((home) => home.linked));
  const applyHomeSections = useMemo(() => {
    if (!page) return { main: [] as ProspectApplicationHome[], hive: [] as ProspectApplicationHome[] };
    const main: ProspectApplicationHome[] = [];
    const hive: ProspectApplicationHome[] = [];
    for (const home of page.homes) {
      if (home.hiveListedUnit) hive.push(home);
      else main.push(home);
    }
    return { main, hive };
  }, [page?.homes]);
  const applyHomesMapSideBySide = useShowApplyHomesMap(Boolean(page?.open));

  useEffect(() => {
    if (!page?.open) return;
    void tenantGoogleMapsLoader.prefetch(['places', 'maps']);
  }, [page?.open]);
  const applyHomesHasRank = Boolean(page?.homes.some((home) => home.shortlistRank));
  const applyHomesHasStatus = Boolean(page?.homes.some((home) => homeStatusLabel(home)));
  const shownBirthday = draft?.isViewer && !birthdayParts.year && !birthdayParts.month && !birthdayParts.day
    ? birthdayPartsFromIso(draft.dateOfBirth)
    : birthdayParts;
  const taskChecks = draft
    ? (draft.isViewer ? liveTaskChecks(draft, shownBirthday) : draft.taskChecks)
    : null;
  const rentTotal = rentShareTotal((draft?.incomeSources ?? []).map((source) => source.rentSharePercent));
  const schoolProgress = draft
    ? (draft.isViewer ? academicChecksFromFields(draft) : draft.academicChecks)
    : null;
  const preloadedProgressByApplicantId = useMemo(() => {
    if (!page) return {};
    return Object.fromEntries(
      page.applicants.map((applicant) => [
        applicant.applicantId,
        applicationProgress(applicant.taskChecks, homeAdded, applicant.academicChecks),
      ]),
    );
  }, [page, homeAdded]);
  const viewerLiveProgress = useMemo(() => {
    if (!draft?.isViewer) return null;
    return applicationProgress(
      liveTaskChecks(draft, shownBirthday),
      homeAdded,
      academicChecksFromFields(draft),
    );
  }, [draft, shownBirthday, homeAdded]);
  const progressForApplicant = useCallback(
    (applicantId: string): ApplicationProgress => {
      if (draft?.isViewer && draft.applicantId === applicantId && viewerLiveProgress) {
        return viewerLiveProgress;
      }
      return preloadedProgressByApplicantId[applicantId] ?? { done: 0, total: 0, percent: 0 };
    },
    [draft, viewerLiveProgress, preloadedProgressByApplicantId],
  );
  const groupProgress = page
    ? page.applicants.reduce<ApplicationProgress>((sum, applicant) => {
      const part = progressForApplicant(applicant.applicantId);
      return { done: sum.done + part.done, total: sum.total + part.total, percent: 0 };
    }, { done: 0, total: 0, percent: 0 })
    : null;
  if (groupProgress) {
    groupProgress.percent = groupProgress.total === 0 ? 0 : Math.round((groupProgress.done / groupProgress.total) * 100);
  }
  const groupDone = groupProgress?.done ?? null;
  const groupTotal = groupProgress?.total ?? null;

  useEffect(() => {
    if (groupDone === null || groupTotal === null) return;
    setPageProgress('/apply-to-unit', { completed: groupDone, total: groupTotal });
  }, [groupDone, groupTotal, setPageProgress]);
  const submitBlockers = page?.open && draft
    ? submissionBlockers(page.applicants, page.homes, draft, shownBirthday)
    : [];
  const schoolYear = draft ? schoolYearChoice(draft.currentYearOfStudy) : { choice: '', other: '' };
  const schoolYearValue = yearIsOther || schoolYear.choice === 'Other' ? 'Other' : schoolYear.choice;
  const academics = draft ? academicsFromPerson(draft) : null;

  const dayOptions = useMemo(() => {
    if (!shownBirthday.year || !shownBirthday.month) return [];
    const count = daysInMonth(Number(shownBirthday.year), Number(shownBirthday.month));
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - MIN_APPLICANT_AGE_YEARS);
    cutoff.setHours(0, 0, 0, 0);
    return Array.from({ length: count }, (_, index) => {
      const day = String(index + 1).padStart(2, '0');
      const candidate = new Date(`${shownBirthday.year}-${shownBirthday.month}-${day}T12:00:00`);
      if (candidate > cutoff) return null;
      return { value: day, label: String(index + 1) };
    }).filter((option): option is { value: string; label: string } => option !== null);
  }, [shownBirthday.year, shownBirthday.month]);

  function chooseBirthday(partial: Partial<BirthdayParts>) {
    if (!draft) return;
    const next = { ...shownBirthday, ...partial };
    if (!next.year) {
      next.month = '';
      next.day = '';
    }
    if (!next.month) next.day = '';
    if (next.year && next.month && next.day && Number(next.day) > daysInMonth(Number(next.year), Number(next.month))) {
      next.day = '';
    }
    setBirthdayParts(next);
    setDraft({ ...draft, dateOfBirth: birthdayIso(next) });
  }

  useEffect(() => {
    if (!headshotSource) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !busy) setHeadshotSource(null);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [headshotSource, busy]);

  const ownHeadshotId = editable ? draft?.headshotDocumentId ?? null : null;
  const shownHeadshot = ownHeadshotId
    ? cachedProspectDocument(ownHeadshotId)?.objectUrl
      ?? (headshotPreview?.id === ownHeadshotId ? headshotPreview.url : null)
    : null;

  useEffect(() => {
    if (!ownHeadshotId || cachedProspectDocument(ownHeadshotId)) return;
    let cancelled = false;
    void loadProspectDocument(getToken, ownHeadshotId)
      .then((file) => {
        if (!cancelled) setHeadshotPreview({ id: ownHeadshotId, url: file.objectUrl });
      })
      .catch(() => {
        if (!cancelled) setHeadshotPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ownHeadshotId, getToken]);

  const applicantSnapshot = editable && draft ? JSON.stringify(savableApplicantPatch(draft)) : null;
  const profileSave = useAutosave({
    snapshot: applicantSnapshot,
    ready: Boolean(page && draft),
    enabled: editable,
    resetKey: draft?.applicantId ?? null,
    baselineSnapshot: lastSaved.current || null,
    save: async (snapshot, context) => {
      const current = draftRef.current;
      if (!current?.isViewer) return;
      const patch = JSON.parse(snapshot) as SavableApplicantPatch;
      const merged = applyProfile(current, patch);
      setPage((existing) => (existing ? mergeViewerProfile(existing, merged) : existing));
      await saveProspectApplicant(getToken, patch, { keepalive: context.keepalive });
      const latest = draftRef.current?.isViewer ? JSON.stringify(savableApplicantPatch(draftRef.current)) : snapshot;
      if (latest === snapshot) {
        lastSaved.current = snapshot;
        acknowledgeApplicantDraft(current.applicantId, patch);
      }
    },
    onSaved: () => setError(null),
    onError: (err) => {
      setError(
        isAutosaveNetworkError(err)
          ? 'We could not reach the server. Your answers stay on this device and will save when the connection is back.'
          : err instanceof Error
            ? err.message
            : 'Unable to save your application.',
      );
    },
  });

  const applicationCardSlices = draft ? applicantCardSlices(savableApplicantPatch(draft)) : emptyApplicantCardSlices();
  let ackedApplicationSlices: ReturnType<typeof applicantCardSlices> | null = null;
  if (editable && lastSaved.current) {
    try {
      ackedApplicationSlices = applicantCardSlices(JSON.parse(lastSaved.current) as SavableApplicantPatch);
    } catch {
      ackedApplicationSlices = null;
    }
  }
  const referralSnapshot = page?.open
    ? JSON.stringify({ referrerName, referrerSource })
    : null;
  const referralSave = useAutosave({
    snapshot: referralSnapshot,
    ready: Boolean(page?.open),
    enabled: Boolean(page?.open && draft?.isViewer),
    resetKey: page?.applicationId ?? 'referral',
    save: async (snapshot, context) => {
      const payload = JSON.parse(snapshot) as { referrerName: string; referrerSource: string };
      const saved = await saveProspectReferral(getToken, payload, { keepalive: context.keepalive });
      const referralName = saved.referrerName ?? payload.referrerName;
      const referralSource = saved.referrerSource ?? payload.referrerSource ?? '';
      setReferrerName(referralName);
      setReferrerSource(referralSource);
      setPage((current) => (
        current ? { ...current, referralName, referralSource } : current
      ));
    },
    onSaved: () => setError(null),
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Unable to save your referral.');
    },
  });
  const referralCardSave = useCardSaveBadges(
    { referral: referralSnapshot ?? '' },
    referralSave.status,
    Boolean(page?.open),
    page?.applicationId ?? 'referral',
  );

  const applicationCardSave = useCardSaveBadges(
    applicationCardSlices,
    editable ? profileSave.status : 'idle',
    Boolean(page && draft && editable),
    draft?.applicantId ?? null,
    ackedApplicationSlices,
  );

  useEffect(() => {
    if (!editable || !draft) return;
    const patch = savableApplicantPatch(draft);
    if (JSON.stringify(patch) === lastSaved.current) return;
    rememberApplicantDraft(draft.applicantId, patch);
  }, [draft, editable]);

  async function run(action: () => Promise<ProspectApplicationPage>, success?: string) {
    setBusy(true);
    setError(null);
    try {
      await profileSave.flush();
      await referralSave.flush();
      const next = await action();
      fullLoadVersion.current += 1;
      setPage(next);
      if (success === 'Submitted') {
        trackTenantGoogleAdsOnce(GOOGLE_ADS_ONCE_KEYS.APPLICATION_SUBMITTED, GOOGLE_ADS_EVENTS.APPLICATION_SUBMITTED);
      }
      setReferrerName(next.referralName ?? '');
      setReferrerSource(next.referralSource ?? '');
      if (success) setNotice(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  function commitIncomeEdit(
    source: ProspectApplicationIncome,
    row: IncomeDraft,
    options?: { keepOpenIfIncomplete?: boolean },
  ): Promise<void> {
    if (!row.kind || incomeMatches(source, row)) {
      setIncomeEdit(null);
      return Promise.resolve();
    }
    if (!incomeRowReady(row)) {
      if (!options?.keepOpenIfIncomplete) return Promise.resolve();
      return Promise.resolve();
    }
    const kind = row.kind;
    const rowSnapshot = { ...row };
    return run(async () => {
      const next = await updateProspectIncome(getToken, source.id, {
        kind,
        osapCategory: kind === 'OSAP' ? rowSnapshot.osapCategory : undefined,
        label: kind === 'OSAP' || kind === 'SAVINGS' ? undefined : rowSnapshot.label.trim(),
        employerName: kind === 'EMPLOYMENT' ? rowSnapshot.employerName.trim() : undefined,
        rentSharePercent: Number(rowSnapshot.rentSharePercent.replace(/[%\s]/g, '')),
      });
      setIncomeEdit((current) => (current?.id === source.id ? null : current));
      return next;
    });
  }

  function commitPendingIncomeEdit(options?: { keepOpenIfIncomplete?: boolean }): Promise<void> {
    const edit = incomeEditRef.current;
    const currentDraft = draftRef.current;
    if (!edit || !currentDraft) return Promise.resolve();
    const source = currentDraft.incomeSources.find((item) => item.id === edit.id);
    if (!source) return Promise.resolve();
    return commitIncomeEdit(source, edit.draft, options);
  }

  const handleSelectApplicant = useCallback(
    (applicantId: string) => {
      if (applicantId === draftRef.current?.applicantId) return;
      void (async () => {
        await commitPendingIncomeEdit();
        await profileSave.flush();
        await referralSave.flush();
        setSelectedId(applicantId);
      })();
    },
    [profileSave],
  );

  useEffect(() => {
    const flushIncome = () => {
      void commitPendingIncomeEdit();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushIncome();
    };
    const onOnline = () => {
      void profileSave.flush();
      void referralSave.flush();
    };
    window.addEventListener('pagehide', flushIncome);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('pagehide', flushIncome);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, [profileSave]);

  function openIncomeField(source: ProspectApplicationIncome, field: 'kind' | 'details' | 'share') {
    if (source.kind === 'SAVINGS' && field === 'details') return;
    const pending = incomeEditRef.current;
    if (pending && (pending.id !== source.id || pending.field !== field)) {
      const pendingSource = draft?.incomeSources.find((item) => item.id === pending.id);
      if (pendingSource) {
        commitIncomeEdit(pendingSource, pending.draft, { keepOpenIfIncomplete: true });
      }
    }
    setIncomeEdit((current) => (
      current?.id === source.id
        ? { ...current, field }
        : { id: source.id, field, draft: draftFromIncome(source) }
    ));
  }

  function changeIncomeKind(source: ProspectApplicationIncome, kind: ProspectIncomeKind) {
    const current = incomeEdit?.id === source.id ? incomeEdit.draft : draftFromIncome(source);
    const next = draftForKind(kind, current);
    if (incomeRowReady(next)) {
      commitIncomeEdit(source, next);
      return;
    }
    setIncomeEdit({ id: source.id, field: 'details', draft: next });
  }

  function beginHeadshot(file: File | undefined) {
    if (!file || !editable) return;
    const extension = file.name.toLowerCase().split('.').pop() ?? '';
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(extension) || (file.type && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type))) {
      setError('Choose a JPEG, PNG, or WebP photo.');
      return;
    }
    setError(null);
    setHeadshotSource(file);
  }

  async function adjustHeadshot() {
    if (!draft?.headshotDocumentId) return;
    setError(null);
    try {
      setHeadshotSource(await fetchProspectApplicationFile(getToken, draft.headshotDocumentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to open that photo.');
    }
  }

  async function upload(kind: ProspectApplicationDocumentKind, file: File | undefined) {
    if (!file || !editable || kind === 'photo-id') return;
    await run(() => uploadProspectApplicationFile(getToken, file, kind), 'Uploaded');
  }

  async function downloadFile(documentId: string | null, filename: string) {
    if (!documentId) return;
    setError(null);
    try {
      const blob = await fetchProspectApplicationFile(getToken, documentId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to download that file.');
    }
  }

  return (
    <div className={`${SHELL} flex flex-col gap-4 py-2 sm:py-4`}>
      <header className={CARD}>
        <div className="flex flex-col gap-5 min-[800px]:flex-row min-[800px]:items-start min-[800px]:gap-10">
          <div className="min-w-0 min-[800px]:flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 6</p>
            <h1 className="mt-1 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">Apply to a Unit</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:text-base">
              Fill this in together. Each person can see how the others are progressing.
            </p>
            {notice ? <p className="mt-2 text-sm text-emerald-700">{notice}</p> : null}
            {error ? <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
          </div>
          {groupProgress && draft && page?.open ? (
            <div className="w-full shrink-0 min-[800px]:w-[min(42rem,56%)]">
              <ProgressTrack
                label="Application Progress"
                progress={groupProgress}
                action={(
                  <SubmitApplicationButton
                    busy={busy}
                    blockers={submitBlockers}
                    onSubmit={() => void run(() => submitProspectApplication(getToken), 'Submitted')}
                  />
                )}
              />
            </div>
          ) : null}
        </div>
      </header>

      {!page ? <p className="text-sm text-gray-500 dark:text-gray-400">Loading your application…</p> : null}

      {page && !page.open ? (
        <section className={CARD}>
          <p className="text-sm text-gray-700 dark:text-gray-300">{page.reason}</p>
        </section>
      ) : null}

      {page?.open && draft ? (
        <>
          <ProspectPersonSwitcher
            people={page.applicants.map((applicant) => ({
              id: applicant.applicantId,
              displayName: applicant.displayName,
              isViewer: applicant.isViewer,
              headshotDocumentId: applicant.headshotDocumentId,
            }))}
            selectedId={draft.applicantId}
            progressForPerson={progressForApplicant}
            onSelect={handleSelectApplicant}
          />

          {!draft.isViewer ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              You can see {draft.displayName}&apos;s contact information. Every other field shows only whether it is complete.
            </p>
          ) : null}

          <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 min-[880px]:grid-cols-3">
            <section className={`${CARD} relative h-full min-h-0`}>
              <CardSaveBadge phase={applicationCardSave.contact} complete={Boolean(taskChecks && contactSectionDone(taskChecks))} />
              <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Contact information</h2>
              <div className="mt-3 grid gap-3">
                <Field label="First name">
                  <input className={INPUT} disabled={!editable} value={draft.firstName} onChange={(event) => setDraft({ ...draft, firstName: event.target.value })} />
                </Field>
                <Field label="Preferred first name">
                  <input className={INPUT} disabled={!editable} value={draft.preferredFirstName} onChange={(event) => setDraft({ ...draft, preferredFirstName: event.target.value })} />
                </Field>
                <Field label="Last name">
                  <input className={INPUT} disabled={!editable} value={draft.lastName} onChange={(event) => setDraft({ ...draft, lastName: event.target.value })} />
                </Field>
                <PhoneField label="Phone" value={draft.phone} disabled={!editable} onChange={(phone) => setDraft({ ...draft, phone })} />
                <EmailField label="Email" value={draft.email} disabled={!editable} problem={contactEmailProblem} onChange={(email) => setDraft({ ...draft, email })} />
                <div className="flex items-center gap-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-3">
                  {shownHeadshot ? (
                    <img src={shownHeadshot} alt="" className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-white" />
                  ) : (
                    <IncompleteCoApplicantHint
                      done={Boolean(taskChecks?.headshot)}
                      ownerDisplayName={coApplicantOwnerName}
                      className="shrink-0"
                    >
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-gray-400 ring-2 ring-white dark:bg-gray-800 dark:text-gray-500">
                        {editable && !draft.hasHeadshot ? <User size={22} /> : (
                          <span
                            className={`px-1 text-center text-[10px] leading-tight ${editable ? 'text-gray-400 dark:text-gray-500' : statusTextClass(Boolean(taskChecks?.headshot))}`}
                          >
                            {editable ? 'Uploaded' : statusWord(Boolean(taskChecks?.headshot))}
                          </span>
                        )}
                      </div>
                    </IncompleteCoApplicantHint>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className={LABEL}>Headshot</p>
                    {editable ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {draft.headshotDocumentId ? (
                          <button type="button" className="rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-800 px-3 py-2 text-sm" onClick={() => void adjustHeadshot()}>
                            Adjust framing
                          </button>
                        ) : null}
                        <label className="inline-flex cursor-pointer rounded-lg bg-gray-900 px-3 py-2 text-sm text-white">
                          {draft.hasHeadshot ? 'Replace photo' : 'Choose photo'}
                          <input
                            className="sr-only"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(event) => {
                              beginHeadshot(event.target.files?.[0]);
                              event.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <section className={`${CARD} relative flex h-full min-h-0 flex-col sm:col-span-2`}>
              <SectionCheck done={homeAdded && page.applicants.length > 0} />
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Apply to Homes</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Your shortlist, in the order you ranked each home. After a tour at 390 Johnson Street (The Hive), you can
                  add any listed suite in the building. Other homes unlock after their tour ends. A denial stays on
                  the record if you apply again later.
                  {page.denialCount > 0 ? ` Denied ${page.denialCount} time${page.denialCount === 1 ? '' : 's'}.` : ''}
                </p>
                <div className="mt-3 grid min-h-0 flex-1 grid-cols-1 gap-4 sm:min-h-[18rem] sm:grid-cols-2 sm:items-stretch">
                  <div className="min-w-0 self-start">
                    {applyHomeSections.main.length + applyHomeSections.hive.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">Nothing on your shortlist yet.</p>
                    ) : (
                    <ul className="flex min-w-0 flex-col gap-2">
                      {applyHomeSections.main.map((home) => (
                        <ApplyHomePickerRow
                          key={home.unitId}
                          home={home}
                          busy={busy}
                          showRank={applyHomesHasRank}
                          showStatus={applyHomesHasStatus}
                          onToggle={(row) => {
                            void run(() =>
                              row.linked
                                ? removeProspectApplicationUnit(getToken, row.unitId)
                                : addProspectApplicationUnit(getToken, row.unitId),
                            );
                          }}
                        />
                      ))}
                      {applyHomeSections.hive.length > 0 ? (
                        <li className="list-none border-t border-gray-200 pt-3 dark:border-white/10">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                            The Hive
                          </p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Listed suites at 390 Johnson Street, unlocked after your Hive tour.
                          </p>
                        </li>
                      ) : null}
                      {applyHomeSections.hive.map((home) => (
                        <ApplyHomePickerRow
                          key={home.unitId}
                          home={home}
                          busy={busy}
                          showRank={false}
                          showStatus={applyHomesHasStatus}
                          onToggle={(row) => {
                            void run(() =>
                              row.linked
                                ? removeProspectApplicationUnit(getToken, row.unitId)
                                : addProspectApplicationUnit(getToken, row.unitId),
                            );
                          }}
                        />
                      ))}
                    </ul>
                    )}
                  </div>
                  <div
                    className={
                      applyHomesMapSideBySide
                        ? 'relative min-h-64 w-full sm:min-h-0 sm:h-full'
                        : 'relative min-h-64 w-full'
                    }
                  >
                    <ApplicationHomesMap
                      fillHeight={applyHomesMapSideBySide}
                      homes={buildApplicationHomeMapPins(page.homes)}
                      area={page.homes.map((home) => ({ latitude: home.latitude, longitude: home.longitude }))}
                    />
                  </div>
                </div>
              </div>
            </section>

            {draft?.isViewer ? (
              <ReferralCard
                className={`${CARD} relative sm:col-span-2`}
                titleClassName="pr-8 text-base font-semibold text-gray-900 dark:text-white"
                fieldClass={INPUT}
                savePhase={referralCardSave.referral}
                referrerName={referrerName}
                referrerSource={referrerSource}
                onReferrerNameChange={setReferrerName}
                onReferrerSourceChange={setReferrerSource}
              />
            ) : null}

            <section className={`${CARD} relative`}>
              <CardSaveBadge phase={applicationCardSave.basic} complete={Boolean(taskChecks && schoolProgress && basicSectionDone(taskChecks, schoolProgress))} />
              <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Basic information</h2>
              <div className="mt-3 grid gap-3">
                <div>
                  <p className={LABEL}>Birthday</p>
                  {editable ? (
                    <div className="mt-1 grid grid-cols-3 gap-2">
                      <PickList
                        placeholder="Year"
                        value={shownBirthday.year}
                        options={YEAR_OPTIONS}
                        searchable
                        onChange={(year) => chooseBirthday({ year })}
                      />
                      <PickList
                        placeholder="Month"
                        value={shownBirthday.month}
                        options={MONTH_OPTIONS}
                        disabled={!shownBirthday.year}
                        onChange={(month) => chooseBirthday({ month })}
                      />
                      <PickList
                        placeholder="Day"
                        value={shownBirthday.day}
                        options={dayOptions}
                        disabled={!shownBirthday.year || !shownBirthday.month}
                        onChange={(day) => chooseBirthday({ day })}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      <StatusValue label="Year" done={Boolean(taskChecks?.birthYear)} incompleteOwnerName={coApplicantOwnerName} />
                      <StatusValue label="Month" done={Boolean(taskChecks?.birthMonth)} incompleteOwnerName={coApplicantOwnerName} />
                      <StatusValue label="Day" done={Boolean(taskChecks?.birthDay)} incompleteOwnerName={coApplicantOwnerName} />
                    </div>
                  )}
                </div>
                <div>
                  {editable && academics ? (
                    <div className="grid gap-3">
                      <PickList
                        label="Current year of school"
                        placeholder="Year of school"
                        value={schoolYearValue}
                        options={SCHOOL_YEAR_OPTIONS}
                        onChange={(value) => {
                          if (!draft) return;
                          if (value === 'Other') {
                            setYearIsOther(true);
                            if (KNOWN_SCHOOL_YEARS.has(draft.currentYearOfStudy)) {
                              setDraft({ ...draft, currentYearOfStudy: '' });
                            }
                            return;
                          }
                          setYearIsOther(false);
                          setDraft({ ...draft, currentYearOfStudy: value });
                        }}
                      />
                      {schoolYearValue === 'Other' ? (
                        <input
                          className={INPUT}
                          placeholder="Enter your year of school"
                          value={schoolYear.other}
                          onChange={(event) => draft && setDraft({ ...draft, currentYearOfStudy: event.target.value })}
                        />
                      ) : null}
                      <QueensFacultyProgramPicker
                        className="space-y-3"
                        value={academics}
                        onChange={(selection) => draft && setDraft({ ...draft, ...personFieldsFromAcademics(selection) })}
                      />
                    </div>
                  ) : (
                    <div className="mt-3 grid gap-2">
                      <StatusValue label="Current year of school" done={Boolean(draft.academicChecks?.year)} incompleteOwnerName={coApplicantOwnerName} />
                      <StatusValue label="Faculty" done={Boolean(draft.academicChecks?.faculty)} incompleteOwnerName={coApplicantOwnerName} />
                      <StatusValue label="Program" done={Boolean(draft.academicChecks?.program)} incompleteOwnerName={coApplicantOwnerName} />
                      <StatusValue label="Expected graduation" done={Boolean(draft.academicChecks?.graduation)} incompleteOwnerName={coApplicantOwnerName} />
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className={`${CARD} relative`}>
              <CardSaveBadge phase={applicationCardSave.address} complete={Boolean(taskChecks && addressSectionDone(taskChecks))} />
              <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Alternate Contact Address</h2>
              {editable ? (
                <div className="mt-3 grid gap-3">
                  <Field label="Street address">
                    <AddressAutocomplete
                      value={draft.address}
                      onChange={(address) => setDraft({ ...draft, address })}
                      onAddressSelect={(components) => setDraft({
                        ...draft,
                        address: components.address,
                        city: components.city,
                        province: components.province,
                        postalCode: components.postalCode,
                        country: components.country || draft.country,
                      })}
                      className={ADDRESS_INPUT}
                      placeholder="Start typing an address"
                    />
                  </Field>
                  <Field label="Unit">
                    <input className={INPUT} value={draft.unitNumber} onChange={(event) => setDraft({ ...draft, unitNumber: event.target.value })} />
                  </Field>
                  <Field label="City">
                    <input className={INPUT} value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} />
                  </Field>
                  <Field label="Province">
                    <input className={INPUT} value={draft.province} onChange={(event) => setDraft({ ...draft, province: event.target.value })} />
                  </Field>
                  <Field label="Postal code">
                    <input className={INPUT} value={draft.postalCode} onChange={(event) => setDraft({ ...draft, postalCode: event.target.value })} />
                  </Field>
                </div>
              ) : (
                <div className="mt-3 grid gap-3">
                  <StatusValue label="Street address" done={Boolean(taskChecks?.street)} incompleteOwnerName={coApplicantOwnerName} />
                  <StatusValue label="Unit" done={Boolean(taskChecks?.unit)} incompleteOwnerName={coApplicantOwnerName} />
                  <StatusValue label="City" done={Boolean(taskChecks?.city)} incompleteOwnerName={coApplicantOwnerName} />
                  <StatusValue label="Province" done={Boolean(taskChecks?.province)} incompleteOwnerName={coApplicantOwnerName} />
                  <StatusValue label="Postal code" done={Boolean(taskChecks?.postalCode)} incompleteOwnerName={coApplicantOwnerName} />
                </div>
              )}
            </section>

            <section className={`${CARD} relative`}>
              <CardSaveBadge phase={applicationCardSave.emergency} complete={Boolean(taskChecks && emergencySectionDone(taskChecks))} />
              <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Emergency contact</h2>
              {editable ? (
                <div className="mt-3 grid gap-3">
                  <Field label="Name">
                    <input className={INPUT} value={draft.emergencyContactName} onChange={(event) => setDraft({ ...draft, emergencyContactName: event.target.value })} />
                  </Field>
                  <SuggestField
                    label="Relationship"
                    value={draft.emergencyContactRelationship}
                    suggestions={EMERGENCY_RELATIONSHIPS}
                    placeholder="Father, mother, sibling..."
                    onChange={(relationship) => setDraft({ ...draft, emergencyContactRelationship: relationship })}
                  />
                  <PhoneField label="Phone" value={draft.emergencyContactPhone} onChange={(phone) => setDraft({ ...draft, emergencyContactPhone: phone })} />
                  <EmailField label="Email" value={draft.emergencyContactEmail} problem={emergencyEmailProblem} onChange={(email) => setDraft({ ...draft, emergencyContactEmail: email })} />
                </div>
              ) : (
                <div className="mt-3 grid gap-3">
                  <StatusValue label="Name" done={Boolean(taskChecks?.emergencyName)} incompleteOwnerName={coApplicantOwnerName} />
                  <StatusValue label="Relationship" done={Boolean(taskChecks?.emergencyRelationship)} incompleteOwnerName={coApplicantOwnerName} />
                  <StatusValue label="Phone" done={Boolean(taskChecks?.emergencyPhone)} incompleteOwnerName={coApplicantOwnerName} />
                  <StatusValue label="Email" done={Boolean(taskChecks?.emergencyEmail)} incompleteOwnerName={coApplicantOwnerName} />
                </div>
              )}
            </section>

            <section className={`${CARD} relative sm:col-span-2`}>
              <SectionCheck done={Boolean(taskChecks?.income)} />
              <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">How you will pay rent</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                We want to know what your plan is for making your monthly rent payments. How do you plan on funding the monthly obligation?
              </p>
              {editable ? (
                <div className="mt-3 overflow-visible rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-700 text-sm" role="table">
                  <IncomeTableHeader />
                  {draft.incomeSources.map((source) => {
                    const editing = incomeEdit?.id === source.id ? incomeEdit : null;
                    const rowDraft = editing?.draft ?? draftFromIncome(source);
                    return (
                      <div key={source.id} className={`${INCOME_GRID} border-b border-gray-100 dark:border-white/5`} role="row">
                        <IncomeLabeledCell label="Source" className="text-gray-900 dark:text-gray-100">
                          {editing?.field === 'kind' ? (
                            <PickList
                              autoOpen
                              placeholder="Source"
                              value={rowDraft.kind}
                              options={INCOME_OPTIONS}
                              onChange={(value) => changeIncomeKind(source, value as ProspectIncomeKind)}
                              onDismiss={() => commitPendingIncomeEdit()}
                            />
                          ) : (
                            <button type="button" className={INCOME_VALUE} onClick={() => openIncomeField(source, 'kind')}>
                              {INCOME_OPTIONS.find((option) => option.value === source.kind)?.label ?? source.kind}
                            </button>
                          )}
                        </IncomeLabeledCell>
                        <IncomeLabeledCell label="Details" className="text-gray-700 dark:text-gray-300">
                          {editing?.field === 'details' ? (
                            <IncomeDetailFields
                              autoFocus
                              draft={rowDraft}
                              onChange={(next) => {
                                setIncomeEdit({ id: source.id, field: 'details', draft: next });
                                if (next.kind === 'OSAP' && incomeRowReady(next)) commitIncomeEdit(source, next);
                              }}
                              onBlur={() => commitPendingIncomeEdit()}
                            />
                          ) : source.kind === 'SAVINGS' ? (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          ) : (
                            <button type="button" className={`${INCOME_VALUE} text-gray-700 dark:text-gray-300`} onClick={() => openIncomeField(source, 'details')}>
                              {incomeDetailsText(source)}
                            </button>
                          )}
                        </IncomeLabeledCell>
                        <IncomeLabeledCell label="Share of rent" className="tabular-nums text-gray-900 dark:text-gray-100">
                          {editing?.field === 'share' ? (
                            <div className="relative">
                              <input
                                autoFocus
                                className={`${CELL_INPUT} pr-7`}
                                inputMode="decimal"
                                aria-label="Percentage of rent this covers"
                                value={rowDraft.rentSharePercent}
                                onChange={(event) => setIncomeEdit({ id: source.id, field: 'share', draft: { ...rowDraft, rentSharePercent: event.target.value } })}
                                onBlur={(event) => {
                                  if (event.currentTarget.dataset.revert === '1') return;
                                  const edit = incomeEditRef.current;
                                  if (!edit || edit.id !== source.id) return;
                                  commitIncomeEdit(source, {
                                    ...edit.draft,
                                    rentSharePercent: event.currentTarget.value,
                                  });
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') event.currentTarget.blur();
                                  if (event.key === 'Escape') {
                                    event.currentTarget.dataset.revert = '1';
                                    setIncomeEdit(null);
                                  }
                                }}
                              />
                              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-gray-500">%</span>
                            </div>
                          ) : (
                            <button type="button" className={`${INCOME_VALUE} tabular-nums`} onClick={() => openIncomeField(source, 'share')}>
                              {formatShare(source.rentSharePercent)}
                            </button>
                          )}
                        </IncomeLabeledCell>
                        <IncomeLabeledCell label="Actions" className="text-right max-sm:col-span-full">
                          <button
                            type="button"
                            className="w-full rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700 dark:border-rose-500/30 dark:text-rose-300 sm:w-auto sm:border-0 sm:p-0 sm:text-xs"
                            onClick={() => void run(() => removeProspectIncome(getToken, source.id))}
                          >
                            Remove
                          </button>
                        </IncomeLabeledCell>
                      </div>
                    );
                  })}
                  <div
                    className={`${INCOME_GRID} border-y border-gray-200 bg-gray-200 text-gray-900 dark:border-white/10 dark:bg-gray-600 dark:text-white max-sm:mx-0 max-sm:rounded-none max-sm:border-x-0`}
                    role="row"
                  >
                    <div className={`${INCOME_CELL} font-semibold max-sm:col-span-full`} role="cell">
                      <span className={INCOME_MOBILE_LABEL}>Total</span>
                      <span className="hidden sm:inline">Total</span>
                    </div>
                    <div className={`${INCOME_CELL} hidden sm:block`} role="cell" aria-hidden />
                    <IncomeLabeledCell
                      label="Share of rent"
                      className={`tabular-nums font-semibold max-sm:col-span-full ${rentTotal > 100 ? 'text-rose-700 dark:text-rose-300' : 'text-gray-900 dark:text-white'}`}
                    >
                      {formatShare(rentTotal)}
                      {taskChecks?.income ? null : <span className="ml-1 font-normal text-gray-600 dark:text-gray-400">of 100%</span>}
                    </IncomeLabeledCell>
                    <div className={`${INCOME_CELL} hidden sm:block`} role="cell" aria-hidden />
                  </div>
                  <div className={`${INCOME_GRID} bg-gray-50 dark:bg-gray-600/70`} role="row">
                    <IncomeLabeledCell label="Source">
                      <PickList
                        placeholder="Source"
                        value={incomeDraft.kind}
                        options={INCOME_OPTIONS}
                        onChange={(value) => setIncomeDraft({ ...emptyIncomeDraft(value as ProspectIncomeKind), rentSharePercent: incomeDraft.rentSharePercent })}
                      />
                    </IncomeLabeledCell>
                    <IncomeLabeledCell label="Details">
                      <IncomeDetailFields draft={incomeDraft} onChange={setIncomeDraft} />
                    </IncomeLabeledCell>
                    <IncomeLabeledCell label="Share of rent">
                      <div className="relative">
                        <input
                          className={`${CELL_INPUT} pr-7`}
                          inputMode="decimal"
                          aria-label="Percentage of rent this covers"
                          placeholder="0"
                          value={incomeDraft.rentSharePercent}
                          onChange={(event) => setIncomeDraft({ ...incomeDraft, rentSharePercent: event.target.value })}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-gray-500">%</span>
                      </div>
                    </IncomeLabeledCell>
                    <IncomeLabeledCell label="Add source" className="max-sm:col-span-full sm:text-right">
                      <button
                        type="button"
                        className="w-full rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-40 sm:w-auto"
                        disabled={busy || !incomeRowReady(incomeDraft)}
                        onClick={() => {
                          const kind = incomeDraft.kind;
                          if (!kind) return;
                          void run(async () => {
                            const next = await addProspectIncome(getToken, {
                              kind,
                              osapCategory: kind === 'OSAP' ? incomeDraft.osapCategory : undefined,
                              label: kind === 'OSAP' || kind === 'SAVINGS' ? undefined : incomeDraft.label.trim(),
                              employerName: kind === 'EMPLOYMENT' ? incomeDraft.employerName.trim() : undefined,
                              rentSharePercent: Number(incomeDraft.rentSharePercent.replace(/[%\s]/g, '')),
                            });
                            setIncomeDraft(emptyIncomeDraft());
                            return next;
                          });
                        }}
                      >
                        Add
                      </button>
                    </IncomeLabeledCell>
                  </div>
                </div>
              ) : coApplicantOwnerName ? (
                <IncomeCoApplicantTable
                  done={Boolean(taskChecks?.income)}
                  incompleteOwnerName={coApplicantOwnerName}
                />
              ) : null}
            </section>

            <section className={`${CARD} relative flex h-full flex-col`}>
              <SectionCheck done={Boolean(taskChecks?.photoId)} />
              <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Government ID</h2>
              {editable ? (
                <div className="mt-3 grid min-h-0 flex-1 grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
                  <div className="flex min-w-0 flex-col gap-3">
                    <aside className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 dark:bg-sky-950/30" role="note">
                      <p className="text-sm leading-relaxed text-sky-950 dark:text-sky-200">
                        We&apos;ll use this document to verify your identity information.
                      </p>
                    </aside>
                    <CompactFileDropzone
                      className="mt-0 flex-1"
                      disabled={busy}
                      accept={DOCUMENT_FILE_ACCEPT}
                      hint={DOCUMENT_FILE_HINT}
                      check={identityFileProblem}
                      onFile={(file) => setPendingId({ file, kind: '' })}
                    />
                    {draft.photoIdDocumentId ? (
                      <div className="min-w-0">
                        {governmentIdTypeLabel(draft.photoIdDocumentName) ? (
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{governmentIdTypeLabel(draft.photoIdDocumentName)}</p>
                        ) : null}
                        <button
                          type="button"
                          className="mt-1 block max-w-full truncate text-left text-sm text-gray-900 underline dark:text-white"
                          onClick={() => void downloadFile(draft.photoIdDocumentId, draft.photoIdDocumentName || 'Government ID')}
                        >
                          {draft.photoIdDocumentName || 'Government ID'}
                        </button>
                        <button
                          type="button"
                          className="mt-3 text-xs text-rose-700 dark:text-rose-300"
                          onClick={() => {
                            const documentId = draft.photoIdDocumentId;
                            if (documentId) void run(() => removeProspectApplicationDocument(getToken, documentId));
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {draft.photoIdDocumentId ? (
                    <DocumentInlinePreview documentId={draft.photoIdDocumentId} title={draft.photoIdDocumentName || 'Government ID'} />
                  ) : (
                    <div className="min-h-52 rounded-xl border border-dashed border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/5" />
                  )}
                </div>
              ) : (
                <IncompleteCoApplicantHint
                  done={Boolean(taskChecks?.photoId)}
                  ownerDisplayName={coApplicantOwnerName}
                  className="mt-3"
                >
                  <div
                    className={`rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm dark:border-white/10 dark:bg-white/5 ${statusTextClass(Boolean(taskChecks?.photoId))}`}
                    role="status"
                  >
                    {statusWord(Boolean(taskChecks?.photoId))}
                  </div>
                </IncompleteCoApplicantHint>
              )}
            </section>

            {page.characterReferencesRequired ? (
              <section className={`${CARD} relative`}>
                <SectionCheck done={(editable ? draft.references.length : draft.referenceCount) > 0} />
                <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Character references</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">A landlord is the best reference. An employer, coach, teacher, professor, mentor, or family friend also works. These are optional.</p>
                {editable ? (
                  <>
                    <ul className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                      {draft.references.map((reference) => {
                        const windowLabel = formatCallWindow(reference.callStartTime, reference.callEndTime);
                        return (
                          <li key={reference.id} className="flex items-start justify-between gap-2">
                            <span>{reference.name} · {reference.relationship}{windowLabel ? ` · ${windowLabel}` : ''}</span>
                            <button type="button" className="text-rose-700 dark:text-rose-300" onClick={() => void run(() => removeProspectReference(getToken, reference.id))}>Remove</button>
                          </li>
                        );
                      })}
                      {draft.references.length === 0 ? <li className="text-gray-500 dark:text-gray-400">None yet.</li> : null}
                    </ul>
                    <div className="mt-3 grid gap-2">
                      <input className={INPUT} placeholder="Name" value={referenceDraft.name} onChange={(event) => setReferenceDraft({ ...referenceDraft, name: event.target.value })} />
                      <PickList
                        placeholder="Relationship"
                        value={referenceDraft.relationship}
                        options={REFERENCE_OPTIONS}
                        onChange={(relationship) => setReferenceDraft({ ...referenceDraft, relationship })}
                      />
                      <input className={INPUT} inputMode="tel" placeholder="Phone" value={formatPhoneInputLive(referenceDraft.phone)} onChange={(event) => setReferenceDraft({ ...referenceDraft, phone: formatPhoneInputLive(event.target.value) })} />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Please arrange and confirm a call window when your reference will be available to pick up a call.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Call window start (ET)">
                          <input className={INPUT} type="time" value={referenceDraft.callStartTime} onChange={(event) => setReferenceDraft({ ...referenceDraft, callStartTime: event.target.value })} />
                        </Field>
                        <Field label="Call window end (ET)">
                          <input className={INPUT} type="time" value={referenceDraft.callEndTime} onChange={(event) => setReferenceDraft({ ...referenceDraft, callEndTime: event.target.value })} />
                        </Field>
                      </div>
                      {referenceDraft.callStartTime && referenceDraft.callEndTime && !callWindowReady(referenceDraft.callStartTime, referenceDraft.callEndTime) ? (
                        <p className="text-xs text-rose-700 dark:text-rose-300">The call needs to end after it starts.</p>
                      ) : null}
                      <button
                        type="button"
                        className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-40"
                        disabled={!referenceDraft.name.trim() || !referenceDraft.relationship || !phoneComplete(referenceDraft.phone) || !callWindowReady(referenceDraft.callStartTime, referenceDraft.callEndTime)}
                        onClick={() => void run(async () => {
                          const next = await addProspectReference(getToken, referenceDraft);
                          setReferenceDraft({ name: '', relationship: '', phone: '', callStartTime: '', callEndTime: '' });
                          return next;
                        })}
                      >
                        Add reference
                      </button>
                      <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                        By adding a reference, you confirm they&apos;ve agreed to be contacted by Amber Peak about your application.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="mt-3 grid gap-2">
                    <StatusValue label="Name" done={(draft.referenceCount ?? 0) > 0} incompleteOwnerName={coApplicantOwnerName} />
                    <StatusValue label="Relationship" done={(draft.referenceCount ?? 0) > 0} incompleteOwnerName={coApplicantOwnerName} />
                    <StatusValue label="Phone" done={(draft.referenceCount ?? 0) > 0} incompleteOwnerName={coApplicantOwnerName} />
                    <StatusValue label="Call window" done={(draft.referenceCount ?? 0) > 0} incompleteOwnerName={coApplicantOwnerName} />
                  </div>
                )}
              </section>
            ) : null}

            <section className={`${CARD} relative min-w-0 sm:col-span-2`}>
              <SectionCheck done={(editable ? draft.supplementalDocuments.length : draft.supplementalCount) > 0} />
              <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Application supplemental documents</h2>
              <div className="mt-3 flex flex-col gap-4">
              <aside className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-500/40 dark:bg-amber-950" role="note">
                <h3 className="text-sm font-semibold text-amber-950 dark:text-amber-100">How Can You Make Your Application Stronger?</h3>
                <p className="mt-1 text-sm font-normal leading-relaxed text-amber-950 dark:text-amber-100">
                  We are looking for responsible and mature individuals who will take pride in the home they live in. We seek character qualities such as professionalism in communication, respect for one&apos;s housemates and neighbors, and a commitment to taking good physical care of the very expensive home they will be given possession of. Any supplemental documents that attest to these qualities can enhance your application and make it more likely to be selected when multiple groups have applied for the same unit.
                </p>
                <p className="mt-3 text-sm font-semibold text-amber-950 dark:text-amber-100">Ideas</p>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-sm font-normal text-amber-950 dark:text-amber-100">
                  {SUGGESTION_OPTIONS.filter((option) => option.value !== 'OTHER').map((option) => (
                    <li key={option.value}>{option.label}</li>
                  ))}
                </ul>
              </aside>
              {editable ? (
                <div className="min-w-0">
                  <CompactFileDropzone
                    disabled={busy}
                    accept={DOCUMENT_FILE_ACCEPT}
                    hint={DOCUMENT_FILE_HINT}
                    check={supplementalFileProblem}
                    onFile={(file) => setPendingSupplemental({ file, type: '', name: '' })}
                  />
                  <UploadedDocumentsTable
                    rows={draft.supplementalDocuments.map((file) => ({
                      key: file.id,
                      documentId: file.documentId,
                      typeLabel: suggestionLabel(file.suggestion),
                      title: file.name,
                    }))}
                    onDownload={(documentId, filename) => void downloadFile(documentId, filename)}
                    onRemove={(documentId) => void run(() => removeProspectApplicationDocument(getToken, documentId))}
                  />
                </div>
              ) : (
                <IncompleteCoApplicantHint
                  done={(draft.supplementalCount ?? 0) > 0}
                  ownerDisplayName={coApplicantOwnerName}
                >
                  <div
                    className={`rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm dark:border-white/10 dark:bg-white/5 ${statusTextClass((draft.supplementalCount ?? 0) > 0)}`}
                    role="status"
                  >
                    {statusWord((draft.supplementalCount ?? 0) > 0)}
                  </div>
                </IncompleteCoApplicantHint>
              )}
              </div>
            </section>

          </div>
          {pendingId ? (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !busy) setPendingId(null);
              }}
            >
              <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="id-title">
                <h2 id="id-title" className="text-base font-semibold text-gray-900 dark:text-white">Label this ID</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Choose whether this is a passport, a driver&apos;s license, or an ID card.</p>
                <p className="mt-3 truncate text-sm text-gray-700 dark:text-gray-300">{pendingId.file.name}</p>
                <div className="mt-3">
                  <PickList
                    label="ID type"
                    placeholder="Passport, driver's license, or ID card"
                    value={pendingId.kind}
                    options={GOVERNMENT_ID_OPTIONS}
                    onChange={(value) => setPendingId({ ...pendingId, kind: value as ProspectGovernmentIdKind })}
                  />
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" className="rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300" disabled={busy} onClick={() => setPendingId(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-40"
                    disabled={busy || !pendingId.kind}
                    onClick={() => {
                      const { file, kind } = pendingId;
                      if (!kind) return;
                      void run(async () => {
                        const next = await uploadProspectApplicationFile(getToken, file, 'photo-id', { idKind: kind });
                        setPendingId(null);
                        return next;
                      }, 'Uploaded');
                    }}
                  >
                    Upload
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {pendingSupplemental ? (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !busy) setPendingSupplemental(null);
              }}
            >
              <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="supplemental-title">
                <h2 id="supplemental-title" className="text-base font-semibold text-gray-900 dark:text-white">Name this document</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Choose what it is, then give it a name. It uploads under that name automatically.</p>
                <p className="mt-3 truncate text-sm text-gray-700 dark:text-gray-300">{pendingSupplemental.file.name}</p>
                <div className="mt-3">
                  <PickList
                    label="Document type"
                    placeholder="Document type"
                    value={pendingSupplemental.type}
                    options={SUGGESTION_OPTIONS}
                    onChange={(value) => setPendingSupplemental({ ...pendingSupplemental, type: value as ProspectSupplementalSuggestion })}
                  />
                </div>
                <label className="mt-3 block">
                  <span className={LABEL}>{pendingSupplemental.type === 'OTHER' ? 'Describe this document' : 'Document name'}</span>
                  <input
                    className={INPUT}
                    value={pendingSupplemental.name}
                    placeholder={pendingSupplemental.type === 'OTHER' ? 'What is this document?' : "Last year's lease"}
                    onChange={(event) => setPendingSupplemental({ ...pendingSupplemental, name: event.target.value })}
                  />
                </label>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" className="rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300" disabled={busy} onClick={() => setPendingSupplemental(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-40"
                    disabled={busy || !pendingSupplemental.type || !pendingSupplemental.name.trim()}
                    onClick={() => {
                      const { file, type, name } = pendingSupplemental;
                      if (!type || !name.trim()) return;
                      void run(async () => {
                        const next = await uploadProspectApplicationFile(getToken, file, 'supplemental', {
                          suggestion: type,
                          documentName: name.trim(),
                        });
                        setPendingSupplemental(null);
                        return next;
                      }, 'Uploaded');
                    }}
                  >
                    Upload
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {headshotSource ? (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !busy) setHeadshotSource(null);
              }}
            >
              <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="headshot-title">
                <h2 id="headshot-title" className="text-base font-semibold text-gray-900 dark:text-white">Frame your headshot</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Drag and zoom until the photo sits in the circle, then tap Use photo to upload.</p>
                <div className="mt-4">
                  <HeadshotFramer
                    source={headshotSource}
                    busy={busy}
                    onCancel={() => setHeadshotSource(null)}
                    onSave={(file) => {
                      setHeadshotSource(null);
                      void upload('headshot', file);
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function emptyApplicantCardSlices(): ReturnType<typeof applicantCardSlices> {
  return applicantCardSlices({});
}

function applicantCardSlices(patch: Partial<SavableApplicantPatch>): {
  contact: string;
  basic: string;
  address: string;
  emergency: string;
} {
  return {
    contact: JSON.stringify({
      firstName: patch.firstName ?? '',
      lastName: patch.lastName ?? '',
      preferredFirstName: patch.preferredFirstName ?? '',
      phone: patch.phone ?? '',
      email: patch.email ?? '',
    }),
    basic: JSON.stringify({
      dateOfBirth: patch.dateOfBirth ?? '',
      school: patch.school ?? '',
      program: patch.program ?? '',
      currentYearOfStudy: patch.currentYearOfStudy ?? '',
      expectedGraduationTerm: patch.expectedGraduationTerm ?? '',
      expectedGraduationYear: patch.expectedGraduationYear ?? '',
    }),
    address: JSON.stringify({
      address: patch.address ?? '',
      city: patch.city ?? '',
      unitNumber: patch.unitNumber ?? '',
      province: patch.province ?? '',
      postalCode: patch.postalCode ?? '',
      country: patch.country ?? '',
    }),
    emergency: JSON.stringify({
      emergencyContactName: patch.emergencyContactName ?? '',
      emergencyContactRelationship: patch.emergencyContactRelationship ?? '',
      emergencyContactPhone: patch.emergencyContactPhone ?? '',
      emergencyContactEmail: patch.emergencyContactEmail ?? '',
    }),
  };
}

function applyProfile(person: ProspectApplicationPerson, profile: SavableApplicantPatch): ProspectApplicationPerson {
  return { ...person, ...profile };
}

function personDraftFromSelection(
  current: ProspectApplicationPerson | null,
  selected: ProspectApplicationPerson,
  lastSaved: { current: string },
  viewerDraftRef: { current: ProspectApplicationPerson | null },
): ProspectApplicationPerson {
  const serverProfile = savableApplicantPatch(selected);
  const serverSnapshot = JSON.stringify(serverProfile);
  const unacked = selected.isViewer ? unackedApplicantPatch(selected.applicantId) : null;
  const unackedSnapshot = unacked ? JSON.stringify(unacked) : null;
  const preferUnacked = Boolean(unacked && unackedSnapshot !== serverSnapshot);
  const remembered = viewerDraftRef.current;
  const rememberedProfile = remembered && remembered.applicantId === selected.applicantId && selected.isViewer
    ? savableApplicantPatch(remembered)
    : null;
  const rememberedSnapshot = rememberedProfile ? JSON.stringify(rememberedProfile) : null;
  const preferRemembered = Boolean(rememberedProfile && rememberedSnapshot !== serverSnapshot);

  if (!current || current.applicantId !== selected.applicantId) {
    if (selected.isViewer && lastSaved.current === '') {
      lastSaved.current = serverSnapshot;
    }
    if (preferUnacked && unacked) return applyProfile(selected, unacked);
    if (preferRemembered && remembered && rememberedProfile) {
      return {
        ...applyProfile(selected, rememberedProfile),
        email: remembered.email,
        emergencyContactEmail: remembered.emergencyContactEmail,
        ...(remembered.dateOfBirth ? { dateOfBirth: remembered.dateOfBirth } : {}),
      };
    }
    return selected;
  }

  const localSnapshot = current.isViewer ? JSON.stringify(savableApplicantPatch(current)) : null;
  const dirty = Boolean(localSnapshot && localSnapshot !== lastSaved.current);
  const keepLocal = dirty || localSnapshot === lastSaved.current;
  const profile = keepLocal
    ? savableApplicantPatch(current)
    : preferUnacked && unacked
      ? unacked
      : preferRemembered && rememberedProfile
        ? rememberedProfile
        : serverProfile;
  return {
    ...applyProfile(current, profile),
    references: selected.references,
    incomeSources: selected.incomeSources,
    supplementalDocuments: selected.supplementalDocuments,
    hasPhotoId: selected.hasPhotoId,
    hasHeadshot: selected.hasHeadshot,
    academicChecks: keepLocal || preferUnacked || preferRemembered
      ? academicChecksFromFields({ ...current, ...profile })
      : selected.academicChecks,
    taskChecks: selected.taskChecks,
    referenceCount: selected.referenceCount,
    supplementalCount: selected.supplementalCount,
    photoIdDocumentId: selected.photoIdDocumentId,
    photoIdDocumentName: selected.photoIdDocumentName,
    headshotDocumentId: selected.headshotDocumentId,
    progress: selected.progress,
    isViewer: selected.isViewer,
  };
}

function applyFriendChecks(page: ProspectApplicationPage, checks: ProspectApplicationChecks): ProspectApplicationPage {
  const byId = new Map(checks.applicants.map((applicant) => [applicant.applicantId, applicant]));
  const linked = new Set(checks.linkedUnitIds);
  let changed = false;
  const applicants = page.applicants.map((applicant) => {
    if (applicant.isViewer) return applicant;
    const next = byId.get(applicant.applicantId);
    if (!next) return applicant;
    const same = applicant.displayName === next.displayName
      && applicant.hasPhotoId === next.hasPhotoId
      && applicant.hasHeadshot === next.hasHeadshot
      && applicant.headshotDocumentId === next.headshotDocumentId
      && applicant.referenceCount === next.referenceCount
      && applicant.supplementalCount === next.supplementalCount
      && JSON.stringify(applicant.taskChecks) === JSON.stringify(next.taskChecks)
      && JSON.stringify(applicant.academicChecks) === JSON.stringify(next.academicChecks);
    if (same) return applicant;
    changed = true;
    return {
      ...applicant,
      displayName: next.displayName,
      hasPhotoId: next.hasPhotoId,
      hasHeadshot: next.hasHeadshot,
      headshotDocumentId: next.headshotDocumentId,
      academicChecks: next.academicChecks,
      taskChecks: next.taskChecks,
      referenceCount: next.referenceCount,
      supplementalCount: next.supplementalCount,
    };
  });
  const homes = page.homes.map((home) => {
    const isLinked = linked.has(home.unitId);
    if (home.linked === isLinked) return home;
    changed = true;
    return { ...home, linked: isLinked };
  });
  return changed ? { ...page, applicants, homes } : page;
}

function mergeViewerProfile(page: ProspectApplicationPage, person: ProspectApplicationPerson): ProspectApplicationPage {
  const profile = savableApplicantPatch(person);
  const taskChecks = liveTaskChecks(person, birthdayPartsFromIso(person.dateOfBirth));
  const academicChecks = academicChecksFromFields(person);
  return {
    ...page,
    applicants: page.applicants.map((applicant) => (
      applicant.applicantId === person.applicantId && applicant.isViewer
        ? { ...applicant, ...profile, taskChecks, academicChecks }
        : applicant
    )),
  };
}

function PhoneField({
  label,
  value,
  disabled = false,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label}>
      <input
        className={INPUT}
        inputMode="tel"
        autoComplete="tel"
        disabled={disabled}
        value={formatPhoneInputLive(value)}
        onChange={(event) => onChange(formatPhoneInputLive(event.target.value))}
      />
    </Field>
  );
}

function EmailField({
  label,
  value,
  disabled = false,
  problem,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  problem: string | null;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label}>
      <input
        className={INPUT}
        type="email"
        inputMode="email"
        autoComplete="email"
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {problem ? <span className="mt-1 block text-xs normal-case tracking-normal text-rose-700 dark:text-rose-300">{problem}</span> : null}
    </Field>
  );
}

function UploadedDocumentsTable({
  rows,
  onDownload,
  onRemove,
}: {
  rows: Array<{ key: string; documentId: string; typeLabel: string; title: string }>;
  onDownload: (documentId: string, filename: string) => void;
  onRemove?: (documentId: string) => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const scrolls = rows.length > 3;
  const rowKey = rows.map((row) => `${row.key}:${row.title}:${row.typeLabel}`).join('\0');

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const measure = () => {
      if (rows.length <= 3) {
        setMaxHeight((current) => (current === null ? current : null));
        return;
      }
      const header = frame.querySelector<HTMLElement>('[data-doc-header]');
      const visible = [...frame.querySelectorAll<HTMLElement>('[data-doc-row]')].slice(0, 3);
      if (!header || visible.length < 3) return;
      const height = header.offsetHeight + visible.reduce((sum, row) => sum + row.offsetHeight, 0);
      setMaxHeight((current) => (current === height ? current : height));
    };
    measure();
    const body = frame.querySelector<HTMLElement>('[data-doc-body]');
    if (!body) return;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [rowKey, rows.length]);

  return (
    <div
      ref={frameRef}
      className={`mt-3 overflow-x-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-700 ${scrolls ? 'overflow-y-auto' : 'overflow-hidden'}`}
      style={scrolls && maxHeight ? { maxHeight } : undefined}
    >
      <div data-doc-header className="sticky top-0 z-10 grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] items-center border-b border-gray-200 bg-white text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-gray-700 dark:text-gray-400">
        <div className="px-3 py-2">Type</div>
        <div className="px-3 py-2">Document</div>
        <div className="px-3 py-2 text-right">Actions</div>
      </div>
      <div data-doc-body>
        {Array.from({ length: Math.max(rows.length, 3) }, (_, index) => {
          const row = rows[index];
          if (!row) {
            return (
              <div key={`empty-${index}`} className="grid h-10 grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] items-center border-b border-gray-100 last:border-b-0 dark:border-white/5">
                <div className="px-3 text-gray-300 dark:text-gray-500">—</div>
                <div className="px-3 text-gray-300 dark:text-gray-500">—</div>
                <div className="px-3" />
              </div>
            );
          }
          return (
            <div data-doc-row key={row.key} className="grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] items-start gap-x-2 border-b border-gray-100 py-2 last:border-b-0 dark:border-white/5">
              <div className="min-w-0 break-words px-3 text-sm text-gray-700 dark:text-gray-200">{row.typeLabel || '—'}</div>
              <div className="min-w-0 px-3">
                <DocumentHoverPreview
                  documentId={row.documentId}
                  title={row.title}
                  className="whitespace-normal break-words"
                  onOpen={() => onDownload(row.documentId, row.title)}
                />
              </div>
              <div className="shrink-0 whitespace-nowrap px-3 text-right">
                {onRemove ? (
                  <button type="button" className="text-xs text-rose-700 dark:text-rose-300" onClick={() => onRemove(row.documentId)}>Remove</button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
